/**
  * Firebase configuration for Instagram Commentor
  *
  * Configuration is fetched from the backend API at /api/config/frontend
  * which reads from config/config.yaml. This keeps all config in one place.
  *
  * The config values are public identifiers (not secrets):
  * - apiKey: Firebase API key (safe to expose)
  * - authDomain: Firebase auth domain
  * - projectId: Firebase project ID
  */

import { initializeApp, FirebaseApp, getApps } from 'firebase/app';
import { getAuth, GoogleAuthProvider, OAuthProvider, Auth, signInAnonymously as firebaseSignInAnonymously } from 'firebase/auth';
import { getFirestore, Firestore } from 'firebase/firestore';

// API base URL (REACT_APP_API_URL includes /api suffix, e.g. https://example.com/api)
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Cached config
let cachedConfig: { apiKey: string; authDomain: string; projectId: string } | null = null;

// Firebase instances
let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let googleProvider: GoogleAuthProvider | null = null;
let appleProvider: OAuthProvider | null = null;
let firestore: Firestore | null = null;

/**
  * Fetch Firebase config from backend API
  */
async function fetchFirebaseConfig(): Promise<{ apiKey: string; authDomain: string; projectId: string }> {
    if (cachedConfig) {
        return cachedConfig;
    }

    try {
        const response = await fetch(`${API_BASE_URL}/config/frontend`);
        if (!response.ok) {
            throw new Error(`Failed to fetch config: ${response.status}`);
        }
        const data = await response.json();
        cachedConfig = data.firebase;
        return cachedConfig!;
    } catch (error) {
        console.error('Failed to fetch Firebase config from backend:', error);
        // Fallback to environment variables
        return {
            apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
            authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
            projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || 'followerfarm',
        };
    }
}

/**
  * Initialize Firebase with config from backend
  * This should be called before using auth
  */
export async function initializeFirebase(): Promise<void> {
    // Skip Firebase initialization during react-snap pre-rendering
    if (typeof navigator !== 'undefined' && navigator.userAgent.includes('ReactSnap')) {
        return;
    }

    if (app && auth && googleProvider && appleProvider && firestore) {
        return; // Already initialized
    }

    const config = await fetchFirebaseConfig();

    // Validate config before initializing
    if (!config.apiKey || !config.authDomain || !config.projectId) {
        console.error('Firebase config is incomplete:', {
            hasApiKey: !!config.apiKey,
            hasAuthDomain: !!config.authDomain,
            hasProjectId: !!config.projectId,
        });
        throw new Error(
            'Firebase configuration is missing. Make sure the backend is running at ' +
            API_BASE_URL +
            ' and returns valid Firebase config from /api/config/frontend'
        );
    }

    // Check if Firebase is already initialized
    if (getApps().length > 0) {
        app = getApps()[0];
    } else {
        app = initializeApp({
            apiKey: config.apiKey,
            authDomain: config.authDomain,
            projectId: config.projectId,
        });
    }

    auth = getAuth(app);
    googleProvider = new GoogleAuthProvider();
    googleProvider.setCustomParameters({
        prompt: 'select_account',
    });
    appleProvider = new OAuthProvider('apple.com');
    appleProvider.addScope('email');
    appleProvider.addScope('name');
    firestore = getFirestore(app);
}

/**
  * Get Firebase Auth instance
  * Throws if Firebase is not initialized
  */
export function getFirebaseAuth(): Auth {
    if (!auth) {
        throw new Error('Firebase not initialized. Call initializeFirebase() first.');
    }
    return auth;
}

/**
  * Get Google Auth Provider
  * Throws if Firebase is not initialized
  */
export function getGoogleProvider(): GoogleAuthProvider {
    if (!googleProvider) {
        throw new Error('Firebase not initialized. Call initializeFirebase() first.');
    }
    return googleProvider;
}

/**
  * Get Apple Auth Provider
  * Throws if Firebase is not initialized
  */
export function getAppleProvider(): OAuthProvider {
    if (!appleProvider) {
        throw new Error('Firebase not initialized. Call initializeFirebase() first.');
    }
    return appleProvider;
}

/**
  * Check if Firebase is initialized
  */
export function isFirebaseInitialized(): boolean {
    return app !== null && auth !== null;
}

/**
  * Get Firestore instance
  * Throws if Firebase is not initialized
  */
export function getFirestoreDb(): Firestore {
    if (!firestore) {
        throw new Error('Firebase not initialized. Call initializeFirebase() first.');
    }
    return firestore;
}

/**
  * Sign in anonymously
  * Creates a temporary anonymous user that can later be upgraded to a Google account
  */
export async function signInAnonymous(): Promise<void> {
    if (!auth) {
        throw new Error('Firebase not initialized. Call initializeFirebase() first.');
    }
    await firebaseSignInAnonymously(auth);
}

// For backwards compatibility, export auth and googleProvider
// These will be null until initializeFirebase() is called
export { auth, googleProvider, appleProvider };
export default app;
